import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Mail, Calendar, Briefcase, ChevronDown, ChevronUp, LogOut } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Admin = () => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check authentication on mount
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/login');
      return;
    }
    setIsAuthenticated(true);
    fetchSubmissions();
  }, [navigate]);

  const fetchSubmissions = async () => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/login');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/contact/submissions?token=${token}`);
      if (response.status === 401) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminEmail');
        navigate('/login');
        return;
      }
      if (!response.ok) throw new Error('Failed to fetch submissions');
      const data = await response.json();
      // Sort by timestamp descending (newest first)
      data.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setSubmissions(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      try {
        await fetch(`${API_URL}/api/auth/logout?token=${token}`, { method: 'POST' });
      } catch (e) {
        // Ignore logout errors
      }
    }
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminEmail');
    navigate('/login');
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getServiceLabel = (value) => {
    const labels = {
      'workshops': 'Cultural & Identity Workshops',
      'events': 'Event Planning & Cultural Programming',
      'career': 'Career Coaching & Global Mobility',
      'data': 'Data Consulting',
      'tech': 'Technology Consulting',
      'app': 'App & Digital Product Design',
      'cultural-project': 'Share a Cultural Project',
      'other': 'Other'
    };
    return labels[value] || value;
  };

  if (!isAuthenticated) {
    return null;
  }

  const adminEmail = localStorage.getItem('adminEmail');

  return (
    <div className="bg-black min-h-screen pt-24 lg:pt-32">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-14">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-12">
          <div>
            <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-white/40 mb-4">
              Admin Dashboard
            </p>
            <h1 className="font-serif text-4xl md:text-5xl text-white font-normal tracking-tight">
              Contact Submissions
            </h1>
            {adminEmail && (
              <p className="font-sans text-sm text-white/50 mt-2">
                Logged in as {adminEmail}
              </p>
            )}
          </div>
          <div className="flex gap-4">
            <button
              onClick={fetchSubmissions}
              disabled={loading}
              className="inline-flex items-center gap-2 px-6 py-3 border border-white/20 text-white font-sans text-[11px] tracking-[0.15em] uppercase hover:bg-white hover:text-black transition-all disabled:opacity-50"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 text-white font-sans text-[11px] tracking-[0.15em] uppercase hover:bg-red-900/50 transition-all"
            >
              <LogOut size={14} />
              Logout
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white/[0.02] border border-white/10 p-6">
            <p className="font-sans text-[11px] tracking-[0.15em] uppercase text-white/40 mb-2">
              Total Submissions
            </p>
            <p className="font-serif text-4xl text-white">{submissions.length}</p>
          </div>
          <div className="bg-white/[0.02] border border-white/10 p-6">
            <p className="font-sans text-[11px] tracking-[0.15em] uppercase text-white/40 mb-2">
              This Month
            </p>
            <p className="font-serif text-4xl text-white">
              {submissions.filter(s => {
                const date = new Date(s.timestamp);
                const now = new Date();
                return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
              }).length}
            </p>
          </div>
          <div className="bg-white/[0.02] border border-white/10 p-6">
            <p className="font-sans text-[11px] tracking-[0.15em] uppercase text-white/40 mb-2">
              Latest
            </p>
            <p className="font-serif text-lg text-white truncate">
              {submissions.length > 0 ? submissions[0]?.name : 'N/A'}
            </p>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="p-6 bg-red-900/20 border border-red-500/30 text-red-300 mb-8">
            <p className="font-sans text-sm">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <RefreshCw size={32} className="text-white/40 animate-spin" />
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && submissions.length === 0 && (
          <div className="text-center py-20 border border-white/10">
            <Mail size={48} className="text-white/20 mx-auto mb-4" />
            <p className="font-serif text-2xl text-white/60">No submissions yet</p>
            <p className="font-sans text-sm text-white/40 mt-2">
              Contact form submissions will appear here
            </p>
          </div>
        )}

        {/* Submissions List */}
        {!loading && !error && submissions.length > 0 && (
          <div className="space-y-4">
            {submissions.map((submission) => (
              <div
                key={submission.id}
                className="bg-white/[0.02] border border-white/10 overflow-hidden"
              >
                {/* Header Row */}
                <button
                  onClick={() => setExpandedId(expandedId === submission.id ? null : submission.id)}
                  className="w-full p-6 flex items-center justify-between hover:bg-white/[0.02] transition-colors text-left"
                >
                  <div className="flex items-center gap-6">
                    <div className="w-10 h-10 bg-gradient-to-r from-[#7B1818] to-[#4A0E0E] rounded-full flex items-center justify-center text-white font-serif">
                      {submission.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-sans text-base text-white">{submission.name}</p>
                      <p className="font-sans text-sm text-white/50">{submission.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="hidden md:block text-right">
                      <p className="font-sans text-xs text-white/40">
                        {formatDate(submission.timestamp)}
                      </p>
                      {submission.company && (
                        <p className="font-sans text-xs text-white/30 mt-1">
                          {submission.company}
                        </p>
                      )}
                    </div>
                    {expandedId === submission.id ? (
                      <ChevronUp size={20} className="text-white/40" />
                    ) : (
                      <ChevronDown size={20} className="text-white/40" />
                    )}
                  </div>
                </button>

                {/* Expanded Content */}
                {expandedId === submission.id && (
                  <div className="px-6 pb-6 border-t border-white/10">
                    <div className="pt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Left Column */}
                      <div className="space-y-4">
                        <div>
                          <p className="font-sans text-[11px] tracking-[0.15em] uppercase text-white/40 mb-2">
                            Contact Info
                          </p>
                          <div className="space-y-2">
                            <a
                              href={`mailto:${submission.email}`}
                              className="flex items-center gap-2 text-white hover:text-white/70 transition-colors"
                            >
                              <Mail size={14} className="text-white/40" />
                              <span className="font-sans text-sm">{submission.email}</span>
                            </a>
                            {submission.company && (
                              <div className="flex items-center gap-2 text-white/70">
                                <Briefcase size={14} className="text-white/40" />
                                <span className="font-sans text-sm">{submission.company}</span>
                              </div>
                            )}
                            <div className="flex items-center gap-2 text-white/50">
                              <Calendar size={14} className="text-white/40" />
                              <span className="font-sans text-sm">{formatDate(submission.timestamp)}</span>
                            </div>
                          </div>
                        </div>

                        {submission.services && submission.services.length > 0 && (
                          <div>
                            <p className="font-sans text-[11px] tracking-[0.15em] uppercase text-white/40 mb-2">
                              Services Interested In
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {submission.services.map((service, i) => (
                                <span
                                  key={i}
                                  className="px-3 py-1 bg-white/5 border border-white/10 font-sans text-xs text-white/70"
                                >
                                  {getServiceLabel(service)}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Right Column */}
                      <div className="space-y-4">
                        {submission.project_description && (
                          <div>
                            <p className="font-sans text-[11px] tracking-[0.15em] uppercase text-white/40 mb-2">
                              Project Description
                            </p>
                            <p className="font-sans text-sm text-white/70 leading-relaxed bg-white/[0.02] p-4 border-l-2 border-[#7B1818]">
                              {submission.project_description}
                            </p>
                          </div>
                        )}

                        <div>
                          <p className="font-sans text-[11px] tracking-[0.15em] uppercase text-white/40 mb-2">
                            Message
                          </p>
                          <p className="font-sans text-sm text-white/70 leading-relaxed bg-white/[0.02] p-4 border-l-2 border-[#7B1818]">
                            {submission.message}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="mt-6 pt-6 border-t border-white/10 flex gap-4">
                      <a
                        href={`mailto:${submission.email}?subject=Re: Your inquiry&body=Hi ${submission.name},%0D%0A%0D%0AThank you for reaching out...`}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#7B1818] to-[#4A0E0E] text-white font-sans text-[11px] tracking-[0.15em] uppercase hover:from-[#8B2020] hover:to-[#5A1010] transition-all"
                      >
                        <Mail size={14} />
                        Reply via Email
                      </a>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
